import React, { useState } from "react";
import { useLangNavigate, Link } from "../i18n/LangContext";
import emailjs from '@emailjs/browser';
import { useTranslation, Trans } from "react-i18next";
import Seo from "../seo/Seo";

const STEP_COLORS = ['#ffca28', '#42a5f5', '#66bb6a', '#ab47bc', '#ffa726'];

const Contact = () => {
  const { t } = useTranslation();
  const help = t("contact.help.items");
  const navigate = useLangNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    projectDescription: "",
    budget: "",
    projectType: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleProjectTypeSelect = (type) => {
    setFormData((prev) => ({ ...prev, projectType: type }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      // Delivered by EmailJS straight from the browser; there is no backend.
      // The sender address and the subject line live in the EmailJS template
      // (template_shltn4a), not here - do not change them without telling
      // Viktor, whose morning brief reads these emails.
      const templateParams = {
        from_name: formData.name.trim(),
        reply_to: formData.email.trim(),
        company: formData.company.trim() || "N/A",
        phone: formData.phone.trim() || "N/A",
        project_type: formData.projectType || "General",
        budget: formData.budget || "N/A",
        message: formData.projectDescription.trim(),
        to_email: "hello@malilabs.com"
      };

      await emailjs.send(
        'service_brttwa9',   // Service ID
        'template_shltn4a',  // Template ID
        templateParams,
        '1_jzerThzEvdUox_y'  // Public key (public by design)
      );

      navigate("/thank-you");
    } catch (err) {
      console.error("Error submitting form:", err);
      setError(t("contact.form.error"));
    } finally {
      // Without this the button stays disabled for good if navigation is blocked.
      setIsSubmitting(false);
    }
  };
 

  return (
    <div className="contact-page">
      <Seo page="contact" path="/contact" />
      <section className="contact_section">
        <div className="container">
          <div className="row justify-content-center mb-5">
            <div className="col-md-10 text-center">
              <div className="heading_container d-flex justify-content-center">
                <h2>{t("contact.title")}</h2>
              </div>
              <p className="text-muted">
                {t("contact.text")}
              </p>
            </div>
          </div>

          <div className="row mb-5">
            <div className="col-12">
              <div className="process-summary-container p-4 bg-light rounded-4 shadow-sm">
                <div className="row text-center g-4">
                  {t("methodology.steps").map((step, i) => (
                    <div key={i} className={`col-md-2 ${i === 0 ? "offset-md-1" : ""}`}>
                      <div className={`process-mini-step ${i === 1 ? "highlighted" : ""}`}>
                        <div className="mini-dot" style={{ backgroundColor: STEP_COLORS[i] }}>{i + 1}</div>
                        <h6>{step.title}</h6>
                        <small>{step.short}</small>
                        <div className="mini-step-tooltip">{step.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-7 mb-4 mb-md-0">
              <div className="contact-form">
                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                <form id="projectForm" onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <h5 id="projectTypeLabel">{t("contact.typeQuestion")}</h5>
                    <div className="row g-3" role="radiogroup" aria-labelledby="projectTypeLabel">
                      {t("contact.types").map((type) => (
                        <div key={type.key} className="col-md-6">
                          <label
                            className={`project-type-card ${formData.projectType === type.key ? "selected" : ""}`}
                          >
                            <input
                              type="radio"
                              name="projectType"
                              value={type.key}
                              checked={formData.projectType === type.key}
                              onChange={() => handleProjectTypeSelect(type.key)}
                              className="project-type-radio"
                            />
                            <h6 className="mb-2">{type.title}</h6>
                            <small className="text-muted">{type.text}</small>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="name" className="form-label">
                          {t("contact.form.name")}
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="company" className="form-label">
                          {t("contact.form.company")}
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label">
                          {t("contact.form.email")}
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="phone" className="form-label">
                          {t("contact.form.phone")}
                        </label>
                        <input
                          type="tel"
                          className="form-control"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="projectDescription" className="form-label">
                      {t("contact.form.description")}
                    </label>
                    <textarea
                      className="form-control"
                      id="projectDescription"
                      name="projectDescription"
                      value={formData.projectDescription}
                      onChange={handleChange}
                      rows="5"
                      placeholder={t("contact.form.descriptionPlaceholder")}
                      required
                    ></textarea>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="budget" className="form-label">
                      {t("contact.form.budget")}
                    </label>
                    <select
                      className="form-select"
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                    >
                      <option value="">{t("contact.form.budgetSelect")}</option>
                      <option value="up to EUR 3,000">{t("contact.form.budgets.upTo3")}</option>
                      <option value="EUR 3,000-12,000">{t("contact.form.budgets.3to12")}</option>
                      <option value="EUR 12,000-25,000">{t("contact.form.budgets.12to25")}</option>
                      <option value="over EUR 25,000">{t("contact.form.budgets.over25")}</option>
                      <option value="not sure yet">{t("contact.form.budgets.notSure")}</option>
                    </select>
                    <p className="form-hint">{t("contact.form.budgetNote")}</p>
                  </div>

                  <p className="form-consent">
                    <Trans
                      i18nKey="contact.form.consent"
                      components={{ 1: <Link to="/privacy" /> }}
                    />
                  </p>

                  <button
                    type="submit"
                    className="btn-submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? t("contact.form.submitting") : t("contact.form.submit")}
                  </button>
                </form>
              </div>
            </div>

            <div className="col-md-5">
              <div className="contact-info-box">
                <h4 className="mb-4">{t("contact.help.title")}</h4>

                <div className="info-item">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                    <line x1="12" y1="22.08" x2="12" y2="12"></line>
                  </svg>
                  <div>
                    <h6 className="mb-1">{help[0].title}</h6>
                    <p className="text-muted mb-0">{help[0].text}</p>
                  </div>
                </div>

                <div className="info-item">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"></polyline>
                    <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"></path>
                  </svg>
                  <div>
                    <h6 className="mb-1">{help[1].title}</h6>
                    <p className="text-muted mb-0">{help[1].text}</p>
                  </div>
                </div>

                <div className="info-item">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <div>
                    <h6 className="mb-1">{help[2].title}</h6>
                    <p className="text-muted mb-0">{help[2].text}</p>
                  </div>
                </div>

                <hr className="my-4" />

                <h5 className="mb-3">{t("contact.help.infoTitle")}</h5>
                <div className="info-item">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  <div>
                    <a
                      href="mailto:hello@malilabs.com"
                      className="text-decoration-none text-muted"
                    >
                      hello@malilabs.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
